<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row class="mt-4 ml-2 mr-2">
        <v-col cols="12">
          <v-card elevation="6" class="header-card">
            <v-card-text class="d-flex align-center py-3">
              <v-btn icon class="mr-2" @click="$router.back()">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-avatar color="primary" size="42" class="mr-3">
                <v-icon dark>mdi-cash-clock</v-icon>
              </v-avatar>
              <div>
                <div class="text-h5 font-weight-bold">{{ form.name || 'Edit Salary Cycle' }}</div>
                <div class="grey--text text-caption">
                  Edit cycle details and manage user assignments
                  <v-chip v-if="cycleCompanyName" x-small color="blue-grey lighten-4" text-color="blue-grey darken-3" label class="ml-2">
                    <v-icon x-small left>mdi-domain</v-icon>
                    {{ cycleCompanyName }}
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-progress-linear v-if="pageLoading" indeterminate color="primary" class="mx-6"></v-progress-linear>

      <template v-else>
        <v-row class="ml-2 mr-2">
          <!-- Edit Form -->
          <v-col md="5">
            <v-card elevation="6" class="form-card">
              <v-card-title class="pb-1">
                <v-icon color="primary" class="mr-2" small>mdi-pencil</v-icon>
                Cycle Details
              </v-card-title>
              <v-card-text>
                <v-form ref="form" v-model="valid">
                  <div class="section-label mb-1">Cycle Name</div>
                  <v-text-field v-model="form.name" outlined dense
                    placeholder="e.g. Standard Monthly, Mid-Month Cycle"
                    :rules="[rules.required]" :error-messages="errors.name"></v-text-field>

                  <div class="section-label mb-1 mt-1">Cycle Period</div>
                  <v-row>
                    <v-col cols="5">
                      <v-select v-model="form.start_day" :items="days" label="Start Day" outlined dense
                        prepend-inner-icon="mdi-calendar-start" :rules="[rules.required]"
                        :error-messages="errors.start_day"></v-select>
                    </v-col>
                    <v-col cols="2" class="d-flex align-center justify-center pb-6">
                      <v-icon color="grey">mdi-arrow-right</v-icon>
                    </v-col>
                    <v-col cols="5">
                      <v-select v-model="form.end_day" :items="days" label="End Day" outlined dense
                        prepend-inner-icon="mdi-calendar-end" :rules="[rules.required]"
                        :error-messages="errors.end_day"></v-select>
                    </v-col>
                  </v-row>

                  <v-expand-transition>
                    <v-alert v-if="form.start_day && form.end_day" dense outlined type="info" class="mb-3">
                      <span v-if="form.start_day <= form.end_day">
                        {{ ordinal(form.start_day) }} to {{ ordinal(form.end_day) }} of same month
                      </span>
                      <span v-else>
                        {{ ordinal(form.start_day) }} prev month to {{ ordinal(form.end_day) }} current month
                      </span>
                    </v-alert>
                  </v-expand-transition>

                  <div class="section-label mb-1">Status</div>
                  <v-switch v-model="form.is_active" color="primary" class="mt-0"
                    :label="form.is_active ? 'Active' : 'Inactive'" hide-details></v-switch>

                  <v-divider class="my-4"></v-divider>

                  <v-btn color="primary" depressed @click="submit" :loading="isLoading" :disabled="!valid" block>
                    <v-icon left>mdi-check</v-icon>
                    Update Salary Cycle
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Assign Users Section -->
          <v-col md="7">
            <v-card elevation="6" class="form-card">
              <v-card-title class="pb-1 d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2" small>mdi-account-group</v-icon>
                  Assigned Users
                  <v-chip small color="primary" dark class="ml-2">{{ assignedUsers.length }}</v-chip>
                </div>
              </v-card-title>
              <v-card-text>
                <!-- Assign Input -->
                <v-row align="center">
                  <v-col cols="8">
                    <v-autocomplete
                      v-model="selectedUsers"
                      :items="employees"
                      label="Search and select users..."
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-account-search"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="4">
                    <v-btn color="primary" depressed block @click="assignUsers" :loading="assignLoading"
                      :disabled="selectedUsers.length === 0">
                      <v-icon left small>mdi-account-plus</v-icon>
                      Assign
                    </v-btn>
                  </v-col>
                </v-row>

                <v-divider class="my-3"></v-divider>

                <!-- Users Table -->
                <div v-if="assignedUsers.length > 0" class="users-table-container">
                  <v-simple-table fixed-header dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">#</th>
                          <th class="text-left">Employee Code</th>
                          <th class="text-left">Employee</th>
                          <th class="text-left">Email</th>
                          <th class="text-center" width="80">Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(user, i) in assignedUsers" :key="`user${i}`">
                          <td class="grey--text">{{ i + 1 }}</td>
                          <td>{{ user.employee_code || '-' }}</td>
                          <td>
                            <div class="d-flex align-center">
                              <v-avatar size="28" color="primary" class="mr-2">
                                <span class="white--text text-caption">{{ user.name ? user.name.charAt(0).toUpperCase() : '?' }}</span>
                              </v-avatar>
                              {{ user.name }}
                            </div>
                          </td>
                          <td class="grey--text">{{ user.email }}</td>
                          <td class="text-center">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" icon x-small @click="removeUser(user.id)">
                                  <v-icon small color="red lighten-1">mdi-close-circle</v-icon>
                                </v-btn>
                              </template>
                              <span>Remove from cycle</span>
                            </v-tooltip>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-8">
                  <v-icon size="48" color="grey lighten-1">mdi-account-off-outline</v-icon>
                  <div class="grey--text mt-2">No users assigned to this cycle</div>
                  <div class="grey--text text--lighten-1 text-caption">Use the search above to add users</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      valid: false,
      isLoading: false,
      pageLoading: true,
      assignLoading: false,
      selectedUsers: [],
      employees: [],
      assignedUsers: [],
      days: Array.from({ length: 31 }, (_, i) => ({
        text: `${i + 1}`,
        value: i + 1,
      })),
      form: {
        name: "",
        start_day: "",
        end_day: "",
        is_active: true,
      },
      cycleCompanyId: null,
      cycleCompanyName: "",
      rules: {
        required: (value) => !!value || "Required.",
      },
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    ordinal(n) {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    async init() {
      // Load the salary cycle first so we know its company_id, then load
      // masters (employees) scoped to that company.
      await this.getData();
      await this.getMasters();
    },
    async getMasters() {
      try {
        const response = await axios.get("/salary_cycles/masters", {
          params: {
            filter_company_id: this.cycleCompanyId || "",
          },
        });
        if (response.data.employees) {
          this.employees = response.data.employees.map((emp) => ({
            text: emp.name,
            value: emp.id,
          }));
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },
    async getData() {
      this.pageLoading = true;
      try {
        const response = await axios.get(`/salary_cycles/${this.$route.params.salaryCycleId}`, {
          params: {
            id: this.$route.params.salaryCycleId,
          },
        });
        if (response.data.success) {
          this.form = {
            name: response.data.data.name,
            start_day: response.data.data.start_day,
            end_day: response.data.data.end_day,
            is_active: Boolean(response.data.data.is_active),
          };
          this.assignedUsers = response.data.data.active_users || [];
          this.cycleCompanyId = response.data.data.company_id;
          this.cycleCompanyName = response.data.data.company ? response.data.data.company.name : '';
        }
      } catch (error) {
        console.error("Error fetching salary cycle:", error);
      } finally {
        this.pageLoading = false;
      }
    },
    async submit() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        try {
          await axios.patch(
            `/salary_cycles/${this.$route.params.salaryCycleId}`,
            {
              ...this.form,
              id: this.$route.params.salaryCycleId,
            }
          );
          this.$router.push("/salary-cycles");
        } catch (error) {
          console.error("Error updating salary cycle:", error);
        } finally {
          this.isLoading = false;
        }
      }
    },
    async assignUsers() {
      this.assignLoading = true;
      try {
        const response = await axios.post("/salary_cycles/assign_users", {
          salary_cycle_id: this.$route.params.salaryCycleId,
          user_ids: this.selectedUsers,
        });
        if (response.data.success) {

          this.selectedUsers = [];
          this.getData();
        }
      } catch (error) {
        console.error("Error assigning users:", error);
      } finally {
        this.assignLoading = false;
      }
    },
    async removeUser(userId) {
      try {
        const response = await axios.post("/salary_cycles/remove_user", {
          salary_cycle_id: this.$route.params.salaryCycleId,
          user_id: userId,
        });
        if (response.data.success) {

          this.getData();
        }
      } catch (error) {
        console.error("Error removing user:", error);
      }
    },
  },
};
</script>

<style scoped>
.header-card {
  border-left: 4px solid #3354f4;
}
.form-card {
  border-radius: 12px;
}
.section-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.users-table-container {
  max-height: 400px;
  overflow-y: auto;
}
</style>
